import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { registerUser } from '../../api/users';
import { setToken } from '../../services/auth';
import { setUser } from '../../features/user/userSlice';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import {
  Container,
  Form,
  ErrorText,
  Chip,
  Title,
  FormBox,
  InnerFormBox,
  TextFieldWrap,
  FormInput,
  InputLabel,
  HelperText,
  SignUpButton,
  BoxRegisterForm,
} from './SignUpForms.styled';
import {
  ValidationAlertIcon,
  ErrorContainer,
} from '../Onboarding/Onboarding.styled';
import './SignUpForms.css';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { ConfirmEmailAddress } from './ConfirmEmailAddress';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';

export const RegisterForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [formSent, setFormSent] = useState(false);

  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [registeredEmail, setRegisteredEmail] = useState('');

  const EmailRegistration = () => {
    setOpenBackdrop(false);
    setFormSent(!formSent);
  };

  const [error, setError] = useState({
    firstName: [],
    lastName: [],
    email: [],
    password1: [],
    password2: [],
    businessName: [],
    nonFieldErrors: [],
  });

  const [localErrors, setLocalErrors] = useState({});

  const validateAlphanumeric = (value, maxLength = 55) => {
    if (!value) return t('Onboarding.validation.required');
    if (value.length > maxLength)
      return t('Onboarding.validation.maxLength', { maxLength });
    if (!/^[a-zA-Z0-9\s]+$/.test(value))
      return t('Onboarding.validation.alphanumeric');
    return '';
  };

  const validateEmail = (value, maxLength = 55) => {
    if (!value) return t('Onboarding.validation.required');
    if (value.length > maxLength)
      return t('Onboarding.validation.maxLength', { maxLength });
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value))
      return t('Onboarding.validation.email');
    return '';
  };

  const validatePassword = (value) => {
    if (!value) return t('Onboarding.validation.required');
    if (value.length < 8) return t('RegisterForm.validation.passwordMinLength');
    if (value.length > 128)
      return t('RegisterForm.validation.passwordMaxLength');
    return '';
  };

  const validatePasswordMatch = (password, confirmPassword) => {
    if (!confirmPassword) return t('Onboarding.validation.required');
    if (password !== confirmPassword)
      return t('RegisterForm.validation.passwordMismatch');
    return '';
  };

  const validateForm = () => {
    const newLocalErrors = {};
    const formData = new FormData(document.querySelector('form'));

    const firstName = formData.get('firstName') || '';
    const lastName = formData.get('lastName') || '';
    const email = formData.get('email') || '';
    const password = formData.get('password') || '';
    const passwordConfirmation = formData.get('password_confirmation') || '';
    const businessName = formData.get('businessName') || '';

    const firstNameError = validateAlphanumeric(firstName, 55);
    if (firstNameError) newLocalErrors.firstName = firstNameError;

    const lastNameError = validateAlphanumeric(lastName, 55);
    if (lastNameError) newLocalErrors.lastName = lastNameError;

    const emailError = validateEmail(email, 55);
    if (emailError) newLocalErrors.email = emailError;

    const passwordError = validatePassword(password);
    if (passwordError) newLocalErrors.password1 = passwordError;

    const passwordMatchError = validatePasswordMatch(
      password,
      passwordConfirmation,
    );
    if (passwordMatchError) newLocalErrors.password2 = passwordMatchError;

    const businessNameError = validateAlphanumeric(businessName, 100);
    if (businessNameError) newLocalErrors.businessName = businessNameError;

    setLocalErrors(newLocalErrors);
    return Object.keys(newLocalErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    if (localErrors[field]) {
      setLocalErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setOpenBackdrop(!openBackdrop);

    try {
      const userToken = await registerUser({
        params: {
          firstName: event.target.firstName.value,
          lastName: event.target.lastName.value,
          username: event.target.email.value,
          email: event.target.email.value,
          password1: event.target.password.value,
          password2: event.target.password_confirmation.value,
          businessName: event.target.businessName.value,
        },
      });
      await setToken(userToken.key);
      setRegisteredEmail(event.target.email.value);
      await setTimeout(EmailRegistration, 4000);
    } catch (err) {
      setOpenBackdrop(false);
      if (err.response.status === 400) {
        const errData = err.response.data;
        setError({
          firstName: errData.firstName || [],
          lastName: errData.lastName || [],
          email: errData.email || errData.username || [],
          password1: errData.password1 || [],
          password2: errData.password2 || [],
          businessName: errData.businessName || [],
          nonFieldErrors: errData.nonFieldErrors || [],
        });
        return;
      }
    }
    dispatch(setUser({ token: 'xxx' }));
  };

  return (
    <>
      {!formSent ? (
        <Container>
          <BoxRegisterForm>
            <Chip>{t('RegisterForm.chip.signUp')}</Chip>
            <Title>{t('RegisterForm.title')}</Title>
            <Form onSubmit={onSubmit}>
              <FormBox>
                <InnerFormBox>
                  <InputLabel>{t('User.firstName.label')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={
                        error.firstName.length > 0 || localErrors.firstName
                      }
                      type="text"
                      placeholder={t('User.firstName.label')}
                      name="firstName"
                      autoComplete="off"
                      maxLength="55"
                      onChange={handleInputChange('firstName')}
                      required
                    />
                    {(error.firstName.length > 0 || localErrors.firstName) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.firstName.length > 0
                            ? error.firstName[0]
                            : localErrors.firstName}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>
                <InnerFormBox>
                  <InputLabel>{t('User.lastName.label')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={error.lastName.length > 0 || localErrors.lastName}
                      type="text"
                      placeholder={t('User.lastName.label')}
                      name="lastName"
                      autoComplete="off"
                      maxLength="55"
                      onChange={handleInputChange('lastName')}
                      required
                    />
                    {(error.lastName.length > 0 || localErrors.lastName) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.lastName.length > 0
                            ? error.lastName[0]
                            : localErrors.lastName}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>
              </FormBox>

              <FormBox>
                <InnerFormBox>
                  <InputLabel>{t('User.email.label')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={error.email.length > 0 || localErrors.email}
                      type="email"
                      placeholder={t('User.email.label')}
                      name="email"
                      autoComplete="off"
                      maxLength="55"
                      onChange={handleInputChange('email')}
                      required
                    />
                    {(error.email.length > 0 || localErrors.email) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.email.length > 0
                            ? error.email[0]
                            : localErrors.email}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>
              </FormBox>
              <FormBox>
                <InnerFormBox>
                  <InputLabel>{t('RegisterForm.businessName')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={
                        error.businessName.length > 0 ||
                        localErrors.businessName
                      }
                      type="text"
                      placeholder={t('RegisterForm.businessName')}
                      name="businessName"
                      autoComplete="off"
                      maxLength="100"
                      onChange={handleInputChange('businessName')}
                      required
                    />
                    {(error.businessName.length > 0 ||
                      localErrors.businessName) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.businessName.length > 0
                            ? error.businessName[0]
                            : localErrors.businessName}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>
              </FormBox>

              <FormBox>
                <InnerFormBox>
                  <InputLabel>{t('User.password.label')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={
                        error.password1.length > 0 || localErrors.password1
                      }
                      type="password"
                      placeholder={t('User.password.label')}
                      name="password"
                      maxLength="128"
                      onChange={handleInputChange('password1')}
                      required
                    />
                    {(error.password1.length > 0 || localErrors.password1) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.password1.length > 0
                            ? error.password1[0]
                            : localErrors.password1}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>

                <InnerFormBox>
                  <InputLabel>{t('User.confirm_password.label')}</InputLabel>
                  <TextFieldWrap>
                    <FormInput
                      error={
                        error.password2.length > 0 || localErrors.password2
                      }
                      type="password"
                      placeholder={t('User.confirm_password.label')}
                      name="password_confirmation"
                      maxLength="128"
                      onChange={handleInputChange('password2')}
                      required
                    />
                    {(error.password2.length > 0 || localErrors.password2) && (
                      <ErrorContainer>
                        <ValidationAlertIcon>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </ValidationAlertIcon>
                        <ErrorText>
                          {error.password2.length > 0
                            ? error.password2[0]
                            : localErrors.password2}
                        </ErrorText>
                      </ErrorContainer>
                    )}
                  </TextFieldWrap>
                </InnerFormBox>
              </FormBox>
              <FormBox>
                <FormControlLabel
                  control={
                    <>
                      <Checkbox color="primary" required />
                      <span>
                        {t('RegisterForm.agree.partA')}&nbsp;
                        <a
                          href="https://shapya.com/terms/"
                          target="blank"
                          className="agreeTermsAnchor">
                          {t('RegisterForm.agree.partB')}
                        </a>
                      </span>
                    </>
                  }
                />
              </FormBox>
              {error.nonFieldErrors.length > 0 && (
                <ErrorText center>{error.nonFieldErrors[0]}</ErrorText>
              )}
              <br />
              <br />
              <SignUpButton>{t('RegisterForm.button.register')}</SignUpButton>
            </Form>
            <p className="alreadyAccount">
              {t('RegisterForm.alreadyAccount.text')}&nbsp;
              <a href="/" className="loginAnchor">
                {t('RegisterForm.alreadyAccount.login')}
              </a>
            </p>
          </BoxRegisterForm>
        </Container>
      ) : null}
      {formSent ? (
        <ConfirmEmailAddress registeredEmail={registeredEmail} />
      ) : null}
      {openBackdrop ? (
        <Backdrop open className="backdropSignUp">
          <CircularProgress color="primary" size={60} thickness={7} />
        </Backdrop>
      ) : null}
    </>
  );
};
